import { makeVar, useQuery, useReactiveVar } from '@apollo/client';
import { sum } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Queries } from '@shared/api';
import { MBAuthUtil } from '@shared/scenes/auth/util';
import { API } from '@shared/types/api';
import { useCurrentBusinessId } from './useCurrentBusinessId';

// ---------------------------------------------------------------------------
// Reactive variable — channel-level unread counts
//
// Keyed by channel id. Seeded on initial load by useSeedInitialChannelUnreadCounts
// and updated live by the anyChannelUpdated subscription in otherHooks.ts.
//
// TODO(API): Once listChannels returns unreadCount, remove this var and the
// seed hook below. Instead, read unreadCount directly from the ChannelListQuery
// cache (via useChannelsLite) everywhere it is currently read from this var.
// ---------------------------------------------------------------------------
export const channelUnreadCountsVar = makeVar<Record<number, number>>({});

export function useChannelLiveUnreadCount(channelId: number): number | undefined {
  const counts = useReactiveVar(channelUnreadCountsVar);
  return counts[channelId];
}

// ---------------------------------------------------------------------------
// Initial channel unread seed (current business)
//
// Uses legacyListChannelUnreadCounts since listChannels cannot return
// unreadCount today. Call this once on app load alongside useChannelList.
//
// TODO(API): Remove when listChannels supports unreadCount.
// ---------------------------------------------------------------------------

const CHANNEL_UNREAD_SEED_FILTER: API.ChannelUnreadType = 'ALL';

export function useSeedInitialChannelUnreadCounts() {
  const businessId = useCurrentBusinessId();

  const { data } = useQuery<API.LegacyListChannelUnreadCountsQuery>(
    Queries.channel.legacyListChannelUnreadCounts,
    {
      variables: { businessId, filter: CHANNEL_UNREAD_SEED_FILTER },
      skip: !businessId,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    const rows = data?.legacyListChannelUnreadCounts;
    if (!rows) {
      return;
    }
    const initial: Record<number, number> = {};
    for (const row of rows) {
      initial[row.channelId] = row.unreadCount ?? 0;
    }
    // Existing values (written by the subscription) take priority over the
    // initial fetch so we never overwrite a fresher live count.
    channelUnreadCountsVar({ ...initial, ...channelUnreadCountsVar() });
  }, [data]);
}

// ---------------------------------------------------------------------------
// Current-business unread total
// ---------------------------------------------------------------------------

function useCurrentBusinessUnreadCount(): number {
  const counts = useReactiveVar(channelUnreadCountsVar);
  return useMemo(() => Object.values(counts).reduce((total, n) => total + n, 0), [counts]);
}

// ---------------------------------------------------------------------------
// Background-business unread counts (org switcher)
//
// Uses legacyListBusinessUnreadCounts (deprecated; will be replaced by a
// core-api equivalent).
// ---------------------------------------------------------------------------

const BACKGROUND_POLL_INTERVAL_MS = 2 * 60 * 1000;

function useBackgroundBusinessUnreadCounts(): {
  counts: Record<number, number>;
  refetch: VoidFunction;
} {
  const { data, refetch } = useQuery<API.LegacyListBusinessUnreadCountsQuery>(
    Queries.business.legacyListBusinessUnreadCounts,
    {
      // Org login uses a business JWT only until a user is picked; this query
      // expects a user session. Skip when there is no real JWT (authToken may be "").
      skip: !MBAuthUtil.getAuthToken(),
      pollInterval: BACKGROUND_POLL_INTERVAL_MS,
      fetchPolicy: 'network-only',
    },
  );

  const counts: Record<number, number> = useMemo(() => {
    const rows = data?.legacyListBusinessUnreadCounts;
    if (!rows) {
      return {};
    }
    const result: Record<number, number> = {};
    for (const b of rows) {
      result[b.businessId] = b.unreadCount ?? 0;
    }
    return result;
  }, [data]);

  return { counts, refetch };
}

export function useBackgroundBusinessUnreadCount(): number {
  const currentBusinessId = useCurrentBusinessId();
  const { counts } = useBackgroundBusinessUnreadCounts();

  return useMemo(
    () =>
      sum(
        Object.entries(counts)
          .filter(([id]) => Number(id) !== currentBusinessId)
          .map(([, count]) => count),
      ),
    [counts, currentBusinessId],
  );
}

export function useTotalUnreadCount(): number {
  const currentCount = useCurrentBusinessUnreadCount();
  const backgroundCount = useBackgroundBusinessUnreadCount();
  return currentCount + backgroundCount;
}

export function useRefetchBusinessUnreadCounts(): VoidFunction {
  const { refetch } = useBackgroundBusinessUnreadCounts();
  return refetch;
}

export function useBusinessUnreadCount(businessId: number): number {
  const currentBusinessId = useCurrentBusinessId();
  const currentCount = useCurrentBusinessUnreadCount();
  const { counts } = useBackgroundBusinessUnreadCounts();

  if (businessId === currentBusinessId) {
    return currentCount;
  }
  return counts[businessId] ?? 0;
}
